import * as React from 'react';
import propz from 'propz';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ClubProgrammeBookingItem, ClubProgrammeStudentConflict } from 'Src/helpers/service/admin/clubProgrammes';
import { fmtAcceptedAt, fmtTimeRange } from '../helpers/clubProgrammeBookingsBoard';

type Props = {
  isOpen: boolean;
  conflict: ClubProgrammeStudentConflict | null;
  renderItemActionButtons: (item: ClubProgrammeBookingItem) => React.ReactNode;
  onClose: () => void;
};

export const ClubProgrammeBookingConflictModal: React.FC<Props> = ({
  isOpen,
  conflict,
  renderItemActionButtons,
  onClose
}) => {
  if (!isOpen || !conflict) return null;

  return (
    <SimpleModal
      isOpen={true}
      title={`Conflict: ${String(conflict.student?.name || 'Student')}`}
      showFooter={false}
      onCloseClick={onClose}
      customClass="pbBookingModal pbBookingConflictModal"
      dialogStyle={{ maxWidth: 820 }}
    >
      <div className="p-2">
        <div className="mb-3">
          <div className="small text-muted">{String(conflict.dayLabel || conflict.day || '')}</div>
          <div className="small text-muted">
            {Array.isArray(conflict.items) ? conflict.items.length : 0} choice
            {Array.isArray(conflict.items) && conflict.items.length === 1 ? '' : 's'} for this day
          </div>
        </div>

        {!Array.isArray(conflict.items) || !conflict.items.length ? (
          <div className="alert alert-light border mb-0">No conflict details.</div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: 10 }}>
            {conflict.items
              .slice()
              .sort((a, b) => {
                const pa = typeof a.priority === 'number' ? a.priority : 999999;
                const pb = typeof b.priority === 'number' ? b.priority : 999999;
                if (pa !== pb) return pa - pb;

                const aa = a.acceptedAt ? new Date(a.acceptedAt).getTime() : 0;
                const ab = b.acceptedAt ? new Date(b.acceptedAt).getTime() : 0;
                if (aa !== ab) return aa - ab;

                const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return ua - ub;
              })
              .map(item => {
                const allocationOutcome = String(item.schoolAllocation?.allocationOutcome || '').toUpperCase();
                const isSelectedRaw =
                  !!item.schoolAllocation?.isSelected ||
                  String((conflict as any).selectedMessageId || '') === String(item.messageId || '');

                const isWaitingListOutcome = allocationOutcome === 'WAITING_LIST';
                const isConfirmedClub = String(conflict.confirmedClubMessageId || '') === String(item.messageId || '');

                const isConfirmedWaitingList =
                  String(conflict.confirmedWaitingListMessageId || '') === String(item.messageId || '');

                const isSelected = isSelectedRaw && !isConfirmedClub && !isConfirmedWaitingList;

                const isConflicting = !isConfirmedClub;

                const time = fmtTimeRange(item.schedule || {});

                const statusUpper = String(item.invitationStatus || '').toUpperCase();
                const statusBadge =
                  statusUpper === 'ACCEPTED'
                    ? 'badge-success'
                    : statusUpper.startsWith('WAITING_LIST')
                    ? 'badge-warning'
                    : statusUpper === 'PROGRAMME_DRAFT'
                    ? 'badge-info'
                    : 'badge-secondary';

                const placesCount =
                  typeof propz.get(item, ['maxParticipants']) === 'number'
                    ? Number(propz.get(item, ['maxParticipants']))
                    : null;

                const filledCount = Number(propz.get(item, ['filledCount'], 0) || 0);
                const selectedClubCount = Number(propz.get(item, ['selectedClubCount'], 0) || 0);
                const selectedWaitingListCount = Number(propz.get(item, ['selectedWaitingListCount'], 0) || 0);
                const confirmedWaitingListCount = Number(propz.get(item, ['confirmedWaitingListCount'], 0) || 0);

                const remainingPlaces =
                  typeof propz.get(item, ['remainingPlaces']) === 'number'
                    ? propz.get(item, ['remainingPlaces'])
                    : null;

                const isOverCapacity = placesCount !== null && filledCount > placesCount;

                const overCapacityCount = isOverCapacity ? filledCount - placesCount : 0;

                return (
                  <div
                    key={String(item.messageId)}
                    className={[
                      'cpBookingsCard',
                      'cpBookingsModalCard',
                      isConflicting ? 'cpBookingsCard--conflict' : '',
                      isConfirmedClub
                        ? 'cpBookingsCard--confirmedClub'
                        : isConfirmedWaitingList
                        ? 'cpBookingsCard--confirmedWaiting'
                        : isSelected
                        ? isWaitingListOutcome
                          ? 'cpBookingsCard--selectedWaiting'
                          : 'cpBookingsCard--selectedClub'
                        : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="cpBookingsCardTop">
                      <div className="cpBookingsCardMain">
                        <div className="cpBookingsStudentName">{String(item.clubName || 'Club')}</div>

                        <div className="cpBookingsMeta">
                          {time ? (
                            <div>
                              Time: <span className="cpBookingsMetaStrong">{time}</span>
                            </div>
                          ) : null}

                          {typeof item.priority === 'number' ? (
                            <div>
                              Priority: <span className="cpBookingsMetaStrong">P{item.priority}</span>
                            </div>
                          ) : null}

                          {item.acceptedAt ? (
                            <div>
                              Accepted: <span className="cpBookingsMetaStrong">{fmtAcceptedAt(item.acceptedAt)}</span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="cpBookingsSide">
                        {item.invitationStatus ? (
                          <span className={`badge ${statusBadge}`}>{String(item.invitationStatus)}</span>
                        ) : null}

                        {item.invitesOnly ? <span className="badge badge-secondary">Invites only</span> : null}

                        {isSelected && !isConfirmedClub && !isConfirmedWaitingList ? (
                          <span className={`badge ${isWaitingListOutcome ? 'badge-warning' : 'badge-info'}`}>
                            {isWaitingListOutcome ? 'Selected: Waiting list' : 'Selected: Club'}
                          </span>
                        ) : null}

                        {isConfirmedClub ? <span className="badge badge-success">Confirmed: Club</span> : null}

                        {isConfirmedWaitingList ? (
                          <span className="badge badge-warning">Confirmed: Waiting list</span>
                        ) : null}
                      </div>
                    </div>

                    <div className="cpBookingsStats">
                      <div className="cpBookingsStatsRow">
                        <span>
                          Places: <b>{placesCount === null ? 'Unlimited' : placesCount}</b>
                        </span>
                        <span>
                          Filled: <b>{filledCount}</b>
                        </span>
                        <span>
                          Remaining: <b>{remainingPlaces === null ? 'Unlimited' : remainingPlaces}</b>
                        </span>
                        <span>
                          Waiting list: <b>{confirmedWaitingListCount}</b>
                        </span>
                      </div>

                      <div className="cpBookingsStatsRow">
                        <span>
                          Pending club: <b>{selectedClubCount}</b>
                        </span>
                        <span>
                          Pending WL: <b>{selectedWaitingListCount}</b>
                        </span>
                        {isOverCapacity ? (
                          <span className="badge badge-danger">Over by {overCapacityCount}</span>
                        ) : null}
                      </div>
                    </div>

                    <div className="cpBookingsActions cpBookingsActions--modal">
                      {renderItemActionButtons(item as ClubProgrammeBookingItem)}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </SimpleModal>
  );
};

ClubProgrammeBookingConflictModal.displayName = 'ClubProgrammeBookingConflictModal';
